class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    const isMobileFacetForm = facetForm?.id === 'FacetFiltersFormMobile';

    if (isMobileFacetForm) {
      facetForm.addEventListener('change', this.onMobileFormChange.bind(this));
    } else {
      facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    }

    facetForm.addEventListener('submit', this.onSubmitHandler.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;
      const fromCache = FacetFiltersForm.filterData.some(filterDataUrl);

      fromCache
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.restoreMobileFacetDrawer();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.restoreMobileFacetDrawer();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text' || event.target.closest('price-range');

        if (newElementToActivate && !isTextInput) {
          newElementToActivate.focus();
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (!target) return;
      target.innerHTML = html.querySelector(selector).innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    mobileForm?.closest('menu-drawer')?.bindEvents();
  }

  static prepareMobileFacetDrawer(event) {
    const drawer = event?.target?.closest('#FacetFiltersFormMobile')?.closest('menu-drawer');
    if (!drawer) return;

    FacetFiltersForm.mobileFacetDrawer = drawer;
    drawer.dataset.facetRendering = 'true';

    const details = drawer.querySelector('details.mobile-facets__disclosure');
    if (details?.hasAttribute('open')) {
      drawer.dataset.facetWasOpen = 'true';
    }
    drawer.dataset.facetOpenDetailsId = drawer.querySelector('.mobile-facets__details[open]')?.id || '';
  }

  static restoreMobileFacetDrawer() {
    const drawer = FacetFiltersForm.mobileFacetDrawer;
    if (!drawer) return;

    const details = drawer.querySelector('details.mobile-facets__disclosure');
    if (drawer.dataset.facetWasOpen === 'true' && details && !details.hasAttribute('open')) {
      details.setAttribute('open', '');
      details.classList.add('menu-opening');
      document.body.classList.add(`overflow-hidden-${drawer.dataset.breakpoint}`);
    }

    if (drawer.dataset.facetOpenDetailsId) {
      const openDetails = drawer.querySelector(`#${CSS.escape(drawer.dataset.facetOpenDetailsId)}`);
      if (openDetails && !openDetails.hasAttribute('open')) {
        openDetails.setAttribute('open', '');
        openDetails.classList.add('menu-opening');
        openDetails.closest('.has-submenu')?.classList.add('submenu-open');
      }
    }

    delete drawer.dataset.facetRendering;
    delete drawer.dataset.facetWasOpen;
    delete drawer.dataset.facetOpenDetailsId;
    FacetFiltersForm.mobileFacetDrawer = null;
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    form.querySelectorAll('price-range input[name]').forEach((input) => {
      if (input.value.trim() === '') {
        formData.set(input.name, '');
        return;
      }
      const value = PriceRange.parsePrice(input.value, input);
      formData.set(input.name, Number.isFinite(value) ? String(value) : '');
    });
    return new URLSearchParams(formData).toString();
  }

  onMobileFormChange(event) {
    const input = event.target;
    if (!input?.matches('input[type="checkbox"]')) return;

    const label = input.closest('.facets__label, .mobile-facets__label');
    if (label) label.classList.toggle('active', input.checked);

    const form = input.closest('form');
    if (!form) return;

    const searchParams = this.createSearchParams(form);
    this.onSubmitForm(searchParams, event, true);
  }

  onSubmitForm(searchParams, event, keepMobileDrawerOpen = true) {
    if (keepMobileDrawerOpen) FacetFiltersForm.prepareMobileFacetDrawer(event);
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form') || event.currentTarget;
    const isMobile = form?.id === 'FacetFiltersFormMobile';

    if (isMobile && event.type === 'input') return;

    if (isMobile) {
      const searchParams = this.createSearchParams(form);
      this.onSubmitForm(searchParams, event, true);
      return;
    }

    const forms = [];
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');

    sortFilterForms.forEach((form) => {
      if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
        forms.push(this.createSearchParams(form));
      }
    });

    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
window.FacetFiltersForm = FacetFiltersForm;
FacetFiltersForm.setListeners();

(() => {
  const chipScrollSelector = '[data-collection-chip-scroll]';
  const collectionChipNavSelector = '.collection-chip-nav';
  const collectionChipParentSelector = '[data-collection-chip-parent]';
  const collectionChipSubmenuSelector = '[data-collection-chip-submenu]';
  const collectionChipNestedSubmenusSelector = '[data-collection-chip-nested-submenus]';
  const collectionChipInteractiveSelector = '.collection-chip-nav__chip';
  const styleFilterListSelector = '[data-style-filter-list]';
  const styleFilterChipSelector = '[data-style-filter-chip]';
  const chipScrollDragThreshold = 8;
  let activeScrollList = null;
  let activeScrollState = null;

  const getScrollList = (target) => target?.closest?.(chipScrollSelector);

  const getCollectionChipTargetLevel = (element) => element?.dataset.collectionChipTargetLevel || '2';

  const getCollectionChipSubmenusByLevel = (nav, level) =>
    Array.from(nav.querySelectorAll(collectionChipSubmenuSelector)).filter(
      (submenu) => (submenu.dataset.collectionChipLevel || '2') === level
    );

  const getCollectionChipParentsByLevel = (scope, level) =>
    Array.from(scope.querySelectorAll(collectionChipParentSelector)).filter(
      (parent) => getCollectionChipTargetLevel(parent) === level
    );

  const matchesCollectionChipAncestor = (level, element, ancestorHandle) =>
    level !== '3' || element.dataset.collectionChipAncestorHandle === ancestorHandle;

  const setCollectionChipParentState = (parent, isActive) => {
    parent.classList.toggle('collection-chip-nav__chip--active', isActive);
    parent.setAttribute('aria-expanded', isActive ? 'true' : 'false');
  };

  const setCollectionChipSubmenuState = (submenu, isActive) => {
    submenu.classList.toggle('is-active', isActive);
    submenu.hidden = !isActive;
  };

  const updateNestedCollectionChipSubmenus = (scope = document) => {
    scope.querySelectorAll?.(collectionChipNestedSubmenusSelector).forEach((container) => {
      container.hidden = !container.querySelector(`${collectionChipSubmenuSelector}:not([hidden])`);
    });
  };

  const activateCollectionChipSubmenu = (nav, trigger, collapseActive = true, resetDescendants = true) => {
    if (!nav || !trigger) return;

    const handle = trigger.dataset.collectionChipParentHandle;
    const targetLevel = getCollectionChipTargetLevel(trigger);
    const ancestorHandle = trigger.dataset.collectionChipAncestorHandle || '';
    const triggerScope =
      targetLevel === '3'
        ? trigger.closest(`${collectionChipSubmenuSelector}[data-collection-chip-level="2"]`) || nav
        : nav;

    if (!handle) return;

    const activeSubmenu = getCollectionChipSubmenusByLevel(nav, targetLevel).find(
      (submenu) =>
        !submenu.hidden &&
        submenu.dataset.collectionChipParentHandle === handle &&
        matchesCollectionChipAncestor(targetLevel, submenu, ancestorHandle)
    );
    const shouldCollapse = collapseActive && Boolean(activeSubmenu);

    getCollectionChipParentsByLevel(triggerScope, targetLevel).forEach((parent) => {
      const isActive =
        !shouldCollapse &&
        parent.dataset.collectionChipParentHandle === handle &&
        matchesCollectionChipAncestor(targetLevel, parent, ancestorHandle);

      setCollectionChipParentState(parent, isActive);
    });

    getCollectionChipSubmenusByLevel(nav, targetLevel).forEach((submenu) => {
      if (!matchesCollectionChipAncestor(targetLevel, submenu, ancestorHandle)) return;

      setCollectionChipSubmenuState(submenu, !shouldCollapse && submenu.dataset.collectionChipParentHandle === handle);
    });

    if (targetLevel === '2' && resetDescendants) {
      getCollectionChipParentsByLevel(nav, '3').forEach((parent) => {
        setCollectionChipParentState(parent, false);
      });

      getCollectionChipSubmenusByLevel(nav, '3').forEach((submenu) => {
        setCollectionChipSubmenuState(submenu, false);
      });
    }

    updateNestedCollectionChipSubmenus(nav);
  };

  const initializeCollectionChipSubmenus = (scope = document) => {
    scope.querySelectorAll?.(collectionChipNavSelector).forEach((nav) => {
      ['2', '3'].forEach((level) => {
        getCollectionChipSubmenusByLevel(nav, level)
          .filter((submenu) => submenu.classList.contains('is-active') || !submenu.hidden)
          .forEach((submenu) => {
            const trigger = getCollectionChipParentsByLevel(nav, level).find(
              (parent) =>
                parent.dataset.collectionChipParentHandle === submenu.dataset.collectionChipParentHandle &&
                matchesCollectionChipAncestor(level, parent, submenu.dataset.collectionChipAncestorHandle || '')
            );

            if (trigger) activateCollectionChipSubmenu(nav, trigger, false, false);
          });
      });

      updateNestedCollectionChipSubmenus(nav);
    });
  };

  const updateStyleChipState = (list, selectedValue = null) => {
    const filterParam = list?.dataset.styleFilterParam;

    if (!filterParam) return;

    const params = new URLSearchParams(window.location.search);
    const activeValues = params.getAll(filterParam);
    const activeValue = selectedValue ?? activeValues[activeValues.length - 1] ?? '';

    list.querySelectorAll(styleFilterChipSelector).forEach((chip) => {
      const isClearChip = chip.dataset.styleFilterClear === 'true';
      const isActive = isClearChip ? activeValue === '' : chip.dataset.styleFilterValue === activeValue;

      chip.classList.toggle('collection-chip-nav__chip--active', isActive);
      chip.dataset.styleFilterActive = isActive ? 'true' : 'false';

      if (isActive) {
        chip.setAttribute('aria-current', 'page');
      } else {
        chip.removeAttribute('aria-current');
      }
    });
  };

  const updateBrowserUrl = (url, searchParams) => {
    history.pushState({ searchParams }, '', `${url.pathname}${url.search}${url.hash}`);
    FacetFiltersForm.searchParamsPrev = searchParams;
  };

  const renderStyleFilterPage = (url, searchParams) => {
    const productGrid = document.getElementById('product-grid');
    const productGridContainer = document.getElementById('ProductGridContainer');

    if (!productGrid || !productGridContainer || !productGrid.dataset.id || url.pathname !== window.location.pathname) {
      window.location.assign(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    const sectionUrl = `${url.pathname}?section_id=${encodeURIComponent(productGrid.dataset.id)}${
      searchParams ? `&${searchParams}` : ''
    }`;

    productGridContainer.querySelector('.collection')?.classList.add('loading');

    fetch(sectionUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const nextProductGridContainer = parsedHTML.getElementById('ProductGridContainer');

        if (!nextProductGridContainer) {
          window.location.assign(`${url.pathname}${url.search}${url.hash}`);
          return;
        }

        productGridContainer.innerHTML = nextProductGridContainer.innerHTML;

        ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
          const currentCount = document.getElementById(id);
          const nextCount = parsedHTML.getElementById(id);

          if (!currentCount || !nextCount) return;

          currentCount.innerHTML = nextCount.innerHTML;
          currentCount.classList.remove('loading');
        });

        updateBrowserUrl(url, searchParams);
      })
      .catch(() => {
        window.location.assign(`${url.pathname}${url.search}${url.hash}`);
      });
  };

  const endChipScrollDrag = (event) => {
    if (!activeScrollList || !activeScrollState) return;

    activeScrollList.dataset.dragging = 'false';

    if (
      event.pointerId !== undefined &&
      typeof activeScrollList.hasPointerCapture === 'function' &&
      activeScrollList.hasPointerCapture(event.pointerId)
    ) {
      activeScrollList.releasePointerCapture(event.pointerId);
    }

    activeScrollList = null;
    activeScrollState = null;
  };

  document.addEventListener('pointerdown', (event) => {
    const list = getScrollList(event.target);

    if (!list || event.button !== 0) return;

    list.dataset.didDrag = 'false';

    activeScrollList = list;
    activeScrollState = {
      moved: false,
      startScrollLeft: list.scrollLeft,
      startX: event.clientX,
    };

    list.dataset.dragging = 'false';

    // NOTE: pointer capture is intentionally NOT set here. Capturing on
    // pointerdown redirects the synthesized `click` event to the captured
    // list element, which breaks chip clicks on desktop (mouse). Capture is
    // only acquired once an actual drag begins (see pointermove below).
  });

  document.addEventListener('pointermove', (event) => {
    if (!activeScrollList || !activeScrollState) return;

    const distance = event.clientX - activeScrollState.startX;

    if (Math.abs(distance) > chipScrollDragThreshold) {
      activeScrollState.moved = true;
      activeScrollList.dataset.didDrag = 'true';
      activeScrollList.dataset.dragging = 'true';

      // Acquire capture only now that we know it's a drag, not a click.
      if (
        event.pointerId !== undefined &&
        typeof activeScrollList.setPointerCapture === 'function' &&
        !activeScrollList.hasPointerCapture(event.pointerId)
      ) {
        activeScrollList.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
    }

    if (!activeScrollState.moved) return;

    activeScrollList.scrollLeft = activeScrollState.startScrollLeft - distance;
  });

  document.addEventListener('pointerup', endChipScrollDrag);
  document.addEventListener('pointercancel', endChipScrollDrag);

  document.addEventListener(
    'wheel',
    (event) => {
      const list = getScrollList(event.target);

      if (!list || list.scrollWidth <= list.clientWidth) return;

      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        list.scrollLeft += event.deltaX;
        return;
      }

      list.scrollLeft += event.deltaY;
      event.preventDefault();
    },
    { passive: false }
  );

  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target.closest?.(collectionChipInteractiveSelector);
      const list = chip?.closest?.(chipScrollSelector);

      if (list?.dataset.didDrag !== 'true') return;

      event.preventDefault();
      event.stopPropagation();
      list.dataset.didDrag = 'false';
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const parent = event.target.closest?.(collectionChipParentSelector);

      if (!parent) return;

      const nav = parent.closest(collectionChipNavSelector);
      const list = parent.closest(chipScrollSelector);

      if (!nav) return;

      if (list?.dataset.didDrag === 'true') {
        event.preventDefault();
        event.stopPropagation();
        list.dataset.didDrag = 'false';
        return;
      }

      event.preventDefault();
      activateCollectionChipSubmenu(nav, parent);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const chip = event.target.closest?.(styleFilterChipSelector);

      if (!chip) return;

      const list = chip.closest(styleFilterListSelector);

      if (!list) return;

      if (list.dataset.didDrag === 'true') {
        event.preventDefault();
        event.stopPropagation();
        list.dataset.didDrag = 'false';
        return;
      }

      const filterParam = chip.dataset.styleFilterParam || list.dataset.styleFilterParam;

      if (!filterParam) return;

      event.preventDefault();

      const url = new URL(chip.href || window.location.href, window.location.origin);
      const value = chip.dataset.styleFilterValue || '';
      const isClearChip = chip.dataset.styleFilterClear === 'true';
      const isActive = chip.dataset.styleFilterActive === 'true';
      const selectedValue = isClearChip || isActive ? '' : value;

      url.searchParams.delete(filterParam);
      url.searchParams.delete('page');

      if (selectedValue !== '') {
        url.searchParams.set(filterParam, selectedValue);
      }

      updateStyleChipState(list, selectedValue);
      renderStyleFilterPage(url, url.searchParams.toString());
    },
    true
  );

  window.addEventListener('popstate', () => {
    document.querySelectorAll(styleFilterListSelector).forEach((list) => {
      updateStyleChipState(list);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initializeCollectionChipSubmenus(event.target);
  });

  initializeCollectionChipSubmenus();
})();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.sliders = this.querySelectorAll('[data-price-handle]');
    if (this.sliders.length) {
      this.slider = this.querySelector('[data-price-slider]');
      this.priceInputs = this.querySelectorAll('[data-price-value]');
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency: this.slider.dataset.currency || 'EUR',
      });
      this.sliders.forEach((element) => {
        element.addEventListener('input', (event) => {
          event.stopPropagation();
          this.updateSlider(event.currentTarget);
        });
        element.addEventListener('change', (event) => {
          this.updateSlider(event.currentTarget);
          this.closest('facet-filters-form')?.onSubmitHandler(event);
        });
      });
      this.updateSlider();
      return;
    }
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  updateSlider(changedInput) {
    const [minSlider, maxSlider] = this.sliders;
    const limit = Number(maxSlider.max);
    if (changedInput === minSlider) minSlider.value = Math.min(Number(minSlider.value), Number(maxSlider.value));
    if (changedInput === maxSlider) maxSlider.value = Math.max(Number(maxSlider.value), Number(minSlider.value));
    const min = Number(minSlider.value);
    const max = Number(maxSlider.value);
    this.slider.style.setProperty('--price-start', limit ? (min / limit) * 100 + '%' : '0%');
    this.slider.style.setProperty('--price-end', limit ? (max / limit) * 100 + '%' : '0%');
    // Give the lower handle priority when both controls reach the upper edge.
    minSlider.style.zIndex = min === limit ? '2' : '1';
    if (changedInput) {
      this.priceInputs[0].value = min > 0 ? String(min) : '';
      this.priceInputs[1].value = max < limit ? String(max) : '';
    }
    for (const [key, value, control] of [['min', min, minSlider], ['max', max, maxSlider]]) {
      const formatted = this.formatter.format(value);
      this.querySelector('[data-price-output="' + key + '"]').textContent = formatted;
      control.setAttribute('aria-valuetext', formatted);
    }
    minSlider.setAttribute('aria-valuemax', max);
    maxSlider.setAttribute('aria-valuemin', min);
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', PriceRange.parsePrice(maxInput.value, maxInput));
    if (minInput.value) maxInput.setAttribute('data-min', PriceRange.parsePrice(minInput.value, minInput));
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') {
      input.value = '';
      return;
    }
    const value = PriceRange.parsePrice(input.value, input);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    const clamped = Number.isFinite(value) ? Math.min(Math.max(value, min), max) : null;
    input.value = clamped === null ? '' : String(clamped).replace('.', input.dataset.decimalSeparator || '.');
  }

  static parsePrice(value, input) {
    const compact = value.replace(/\s/g, '');
    const normalized = input.dataset.decimalSeparator === ',' && compact.includes(',')
      ? compact.replace(/\./g, '').replace(',', '.')
      : compact.replace(/,/g, '');
    return Number(normalized);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
